import {
  atrPctAt,
  bookFromDensity,
  bookOpposingTap,
  bookZonesAtIndex,
  clampDensity,
  clampFractalWings,
  hideepFromDensity,
  inTradeRange,
  pickBookZone,
  redflagFromDensity,
  resolveBookSidePick,
  scaleHideepMinDist,
  volumeChurnFromDensity,
  type Candle,
  type StrategyKind,
  type TradeRange,
} from '@termix/shared';

/** Williams fractal (same rule as @termix/signals) — kept local; web has no signals dep. */
const isBullFractal = (candles: Candle[], index: number, wings: number): boolean => {
  if (wings <= 0 || index < wings || index + wings >= candles.length) return false;
  const low = candles[index].low;
  for (let i = index - wings; i <= index + wings; i++) {
    if (i === index) continue;
    if (candles[i].low <= low) return false;
  }
  return true;
};

const isBearFractal = (candles: Candle[], index: number, wings: number): boolean => {
  if (wings <= 0 || index < wings || index + wings >= candles.length) return false;
  const high = candles[index].high;
  for (let i = index - wings; i <= index + wings; i++) {
    if (i === index) continue;
    if (candles[i].high >= high) return false;
  }
  return true;
};

export type BtMarker = {
  time: number;
  price: number;
  side: 'long' | 'short' | 'exit';
  label: string;
  /** True for persisted WEEX fills — shown even after range clear. */
  real?: boolean;
};

/** Same fee assumption as engine only-profit (~round-trip). */
const SKETCH_FEE_PCT = 0.08;
const netPnlPct = (side: 'long' | 'short', entry: number, mark: number) => {
  if (!(entry > 0) || !(mark > 0)) return 0;
  const gross = side === 'long' ? ((mark - entry) / entry) * 100 : ((entry - mark) / entry) * 100;
  return gross - SKETCH_FEE_PCT;
};

/**
 * Soft-exit gate — mirrors engine mode A (min only when green).
 * Loss/flat → allow; small green below min → wait; ≥ min → allow.
 */
const onlyProfitAllowsExit = (
  side: 'long' | 'short',
  entry: number,
  mark: number,
  cfg: { enabled?: boolean; minProfitPct?: number } | undefined,
) => {
  if (!cfg?.enabled) return true;
  const net = netPnlPct(side, entry, mark);
  if (!(net > 0)) return true;
  return net >= (cfg.minProfitPct ?? 0);
};

/**
 * Visual sketch inside a trade range (no live PnL).
 * Mirrors engine timing: signal on closed bar `i` → marker on **next bar open** (`i+1`).
 * Respects long/short toggles + density dials (shorts stricter).
 * Only-profit uses the same mode A as live, so sketch ≠ “fantasy exits”.
 */
export const sketchBacktestMarkers = (
  candles: Candle[],
  kind: StrategyKind,
  range: TradeRange | null | undefined,
  params: Record<string, unknown> = {},
): BtMarker[] => {
  if (candles.length < 4) return [];
  const out: BtMarker[] = [];
  const onlyProfit = (params.onlyProfit && typeof params.onlyProfit === 'object')
    ? params.onlyProfit as { enabled?: boolean; minProfitPct?: number }
    : undefined;

  const longOn = params.longEnabled !== false;
  const shortOn = params.shortEnabled === true
    || (kind !== 'redflag_dca' && params.shortEnabled !== false);
  const longDens = clampDensity(params.longTradeDensity, kind === 'redflag_dca' ? 55 : 55);
  const shortDens = clampDensity(params.shortTradeDensity, kind === 'redflag_dca' ? 28 : 24);
  const longVc = volumeChurnFromDensity('long', longDens);
  const shortVc = volumeChurnFromDensity('short', shortDens);
  const hideepLongDens = clampDensity(params.longTradeDensity, 50);
  const hideepShortDens = clampDensity(params.shortTradeDensity, 22);
  const longHd = hideepFromDensity('long', hideepLongDens);
  const shortHd = hideepFromDensity('short', hideepShortDens);
  const longRf = redflagFromDensity('long', longDens);
  const shortRf = redflagFromDensity('short', shortDens);
  const bookLongDens = clampDensity(params.longTradeDensity, 55);
  const bookShortDens = clampDensity(params.shortTradeDensity, 40);
  const longBk = bookFromDensity('long', bookLongDens);
  const shortBk = bookFromDensity('short', bookShortDens);
  // Desk dials present → density owns RSI/dist (ignore stale rsiLongBelow:12 etc.).
  const useHideepDial = params.longTradeDensity != null || params.shortTradeDensity != null;
  const fractalWings = clampFractalWings(params.fractalWings, 1);
  const wantFractal = params.requireFractal === false ? false : fractalWings > 0;


  const smaLen = Number(params.smaLength || 10);
  const rsiLen = Number(params.rsiLength || 5);
  const rsiLow = useHideepDial
    ? Number(longHd.rsiLongBelow ?? 12)
    : Number(params.rsiLongBelow ?? longHd.rsiLongBelow ?? 12);
  const rsiHigh = useHideepDial
    ? Number(shortHd.rsiShortAbove ?? 94)
    : Number(params.rsiShortAbove ?? shortHd.rsiShortAbove ?? 94);
  const minDistLongRef = useHideepDial
    ? longHd.minDistPct
    : Number(params.minDistPct ?? longHd.minDistPct);
  const minDistShortRef = useHideepDial
    ? shortHd.minDistPct
    : Number(params.minDistPctShort ?? shortHd.minDistPct);
  const holdLong = Number(params.holdBars || longVc.holdBars);
  const holdShort = Number(params.holdBars || shortVc.holdBars);
  const lookLong = Number(params.breakoutLookback || longVc.breakoutLookback);
  const lookShort = Number(params.shortBreakoutLookback || shortVc.breakoutLookback);
  const pbLong = Number(params.pullbackPct ?? longVc.pullbackPct) / 100;
  const pbShort = Number(params.shortPullbackPct ?? shortVc.pullbackPct) / 100;

  const closes = candles.map((c) => c.close);
  const smaAt = (i: number) => {
    if (i + 1 < smaLen) return null;
    let s = 0;
    for (let j = i - smaLen + 1; j <= i; j++) s += closes[j];
    return s / smaLen;
  };
  const rsiAt = (i: number) => {
    if (i < rsiLen) return null;
    let gain = 0;
    let loss = 0;
    for (let j = i - rsiLen + 1; j <= i; j++) {
      const d = closes[j] - closes[j - 1];
      if (d >= 0) gain += d;
      else loss -= d;
    }
    if (loss === 0) return 100;
    const rs = gain / loss;
    return 100 - 100 / (1 + rs);
  };

  const maxOrders = Math.max(1, Number(params.maxOrders || (kind === 'redflag_dca' ? 6 : 3)));
  const stepLong = Number(
    kind === 'redflag_dca' ? longRf.stepPercent : (params.stepPercent || longVc.stepPercent),
  ) / 100;
  const stepShort = Number(
    kind === 'redflag_dca' ? shortRf.stepPercent : (params.stepPercent || shortVc.stepPercent),
  ) / 100;
  let open: 'long' | 'short' | null = null;
  let entryPx = 0;
  let held = 0;
  let filled = 0;
  let lastAdd = 0;
  let lastExecBar = -1;
  let bookZoneLo = 0;
  let bookZoneHi = 0;

  const pushExec = (signalBar: number, label: string, priceSide: 'long' | 'short' | 'exit') => {
    const exec = signalBar + 1;
    if (exec >= candles.length - 1) return false;
    const next = candles[exec];
    out.push({
      time: next.time,
      price: next.open,
      side: priceSide,
      label,
    });
    lastExecBar = exec;
    return true;
  };

  const rangeHighLow = (i: number, lookback: number) => {
    let high = -Infinity;
    let low = Infinity;
    for (let j = i - lookback; j < i; j++) {
      if (j < 0) continue;
      high = Math.max(high, candles[j].high);
      low = Math.min(low, candles[j].low);
    }
    return { high, low };
  };

  for (let i = Math.max(smaLen, rsiLen, lookLong, lookShort) + 1; i < candles.length - 1; i++) {
    const c = candles[i];
    if (range && !inTradeRange(c.close, c.time, range)) {
      open = null;
      entryPx = 0;
      held = 0;
      filled = 0;
      lastAdd = 0;
      lastExecBar = -1;
      bookZoneLo = 0;
      bookZoneHi = 0;
      continue;
    }
    const sma = smaAt(i);
    const rsi = rsiAt(i);

    if (open) {
      held += 1;
      if (kind === 'order_book' && bookZoneLo > 0 && bookZoneHi > bookZoneLo) {
        const invalidated = open === 'long' ? c.close < bookZoneLo : c.close > bookZoneHi;
        const oppose = !invalidated && bookOpposingTap(open, candles, i, {
          longTradeDensity: bookLongDens,
          shortTradeDensity: bookShortDens,
        });
        if ((invalidated || oppose) && i > lastExecBar) {
          const execPx = candles[i + 1]?.open ?? c.close;
          if (oppose && !onlyProfitAllowsExit(open, entryPx || lastAdd || c.close, execPx, onlyProfit)) {
            continue;
          }
          const was = open;
          if (pushExec(i, was === 'long' ? '×L' : '×S', 'exit')) {
            open = null;
            entryPx = 0;
            held = 0;
            filled = 0;
            lastAdd = 0;
            bookZoneLo = 0;
            bookZoneHi = 0;
          }
          continue;
        }
      }
      const stepPct = open === 'long' ? stepLong : stepShort;
      const inBookZone = kind !== 'order_book'
        || (bookZoneLo > 0 && c.close >= bookZoneLo && c.close <= bookZoneHi);
      if (filled < maxOrders && lastAdd > 0 && stepPct > 0 && i > lastExecBar && inBookZone) {
        const due = open === 'long'
          ? c.close <= lastAdd * (1 - stepPct)
          : c.close >= lastAdd * (1 + stepPct);
        if (due && pushExec(i, open === 'long' ? '+L' : '+S', open)) {
          filled += 1;
          lastAdd = candles[i + 1].open;
          held = 0;
          continue;
        }
      }
      const holdNeed = open === 'long'
        ? (kind === 'order_book' ? Number(params.holdBars || longBk.holdBars) : holdLong)
        : (kind === 'order_book' ? Number(params.holdBars || shortBk.holdBars) : holdShort);
      const exitSignal =
        (open === 'long' && (
          (kind !== 'order_book' && rsi != null && rsi >= rsiHigh)
          || ((kind === 'volume_churn' || kind === 'order_book') && held >= holdNeed)
        ))
        || (open === 'short' && (
          (kind !== 'order_book' && rsi != null && rsi <= rsiLow)
          || ((kind === 'volume_churn' || kind === 'order_book') && held >= holdNeed)
        ));
      if (exitSignal && i > lastExecBar) {
        const execPx = candles[i + 1]?.open ?? c.close;
        // Mode A: stick only while green-but-below-min; losers exit on signal.
        if (!onlyProfitAllowsExit(open, entryPx || lastAdd || c.close, execPx, onlyProfit)) {
          continue;
        }
        const was = open;
        if (pushExec(i, was === 'long' ? '×L' : '×S', 'exit')) {
          open = null;
          entryPx = 0;
          held = 0;
          filled = 0;
          lastAdd = 0;
          bookZoneLo = 0;
          bookZoneHi = 0;
        }
      }
      continue;
    }

    if (kind === 'order_book') {
      const zones = bookZonesAtIndex(candles, i, {
        longTradeDensity: bookLongDens,
        shortTradeDensity: bookShortDens,
      });
      const bar = { low: c.low, high: c.high, close: c.close };
      const bull = longOn
        ? pickBookZone(zones, 'bull', bar, longBk.minStrength, longBk.allowTested, longBk.approachPct)
        : null;
      const bear = shortOn
        ? pickBookZone(zones, 'bear', bar, shortBk.minStrength, shortBk.allowTested, shortBk.approachPct)
        : null;
      const picked = resolveBookSidePick(bull, bear, c.close);
      if (picked && pushExec(i, picked.side === 'long' ? 'L' : 'S', picked.side)) {
        open = picked.side;
        held = 0;
        filled = 1;
        lastAdd = candles[i + 1].open;
        entryPx = lastAdd;
        bookZoneLo = picked.zone.priceLow;
        bookZoneHi = picked.zone.priceHigh;
      }
      continue;
    }

    if (sma == null || rsi == null) continue;

    if (kind === 'redflag_dca') {
      // Sketch: dip → long grid, rally → short grid (when enabled)
      if (longOn && !shortOn && c.close < sma * (1 - stepLong * 0.35) && pushExec(i, 'DCA', 'long')) {
        open = 'long';
        held = 0;
        filled = 1;
        lastAdd = candles[i + 1].open;
        entryPx = lastAdd;
      } else if (shortOn && !longOn && c.close > sma * (1 + stepShort * 0.35) && pushExec(i, 'DCA↓', 'short')) {
        open = 'short';
        held = 0;
        filled = 1;
        lastAdd = candles[i + 1].open;
        entryPx = lastAdd;
      } else if (longOn && shortOn) {
        if (c.close < sma * (1 - stepLong * 0.35) && pushExec(i, 'DCA', 'long')) {
          open = 'long';
          held = 0;
          filled = 1;
          lastAdd = candles[i + 1].open;
          entryPx = lastAdd;
        } else if (c.close > sma * (1 + stepShort * 0.35) && pushExec(i, 'DCA↓', 'short')) {
          open = 'short';
          held = 0;
          filled = 1;
          lastAdd = candles[i + 1].open;
          entryPx = lastAdd;
        }
      }
      continue;
    }

    // Entry: HiDeep-style OR volume-churn-style depending on kind
    if (kind === 'hideep') {
      const dist = (Math.abs(c.close - sma) / sma) * 100;
      const atr = atrPctAt(candles, i);
      const minDistLong = scaleHideepMinDist(minDistLongRef, atr);
      const minDistShort = scaleHideepMinDist(minDistShortRef, atr);
      if (longOn && c.close < c.open && c.close < sma && rsi <= rsiLow && dist >= minDistLong) {
        if (wantFractal && !isBullFractal(candles, i, fractalWings)) {
          /* skip — wait for Williams confirm */
        } else if (pushExec(i, 'L', 'long')) {
          open = 'long';
          held = 0;
          filled = 1;
          lastAdd = candles[i + 1].open;
          entryPx = lastAdd;
        }
      } else if (shortOn && c.close > c.open && c.close > sma && rsi >= rsiHigh && dist >= minDistShort) {
        if (wantFractal && !isBearFractal(candles, i, fractalWings)) {
          /* skip — wait for Williams confirm */
        } else if (pushExec(i, 'S', 'short')) {
          open = 'short';
          held = 0;
          filled = 1;
          lastAdd = candles[i + 1].open;
          entryPx = lastAdd;
        }
      }
      continue;
    }

    // volume_churn
    let wantLong = false;
    let wantShort = false;
    if (longOn && i >= lookLong) {
      const { high, low } = rangeHighLow(i, lookLong);
      const mid = (high + low) / 2;
      const mode = longVc.entryMode;
      if ((mode === 'breakout' || mode === 'both') && c.close > high) wantLong = true;
      if ((mode === 'pullback' || mode === 'both') && c.close <= mid * (1 - pbLong)) wantLong = true;
    }
    if (shortOn && i >= lookShort) {
      const { high, low } = rangeHighLow(i, lookShort);
      const mid = (high + low) / 2;
      const mode = shortVc.entryMode;
      if ((mode === 'breakout' || mode === 'both') && c.close < low) wantShort = true;
      if ((mode === 'pullback' || mode === 'both') && c.close >= mid * (1 + pbShort)) wantShort = true;
    }
    if (wantLong && !wantShort && pushExec(i, 'L', 'long')) {
      open = 'long';
      held = 0;
      filled = 1;
      lastAdd = candles[i + 1].open;
      entryPx = lastAdd;
    } else if (wantShort && !wantLong && pushExec(i, 'S', 'short')) {
      open = 'short';
      held = 0;
      filled = 1;
      lastAdd = candles[i + 1].open;
      entryPx = lastAdd;
    }
  }

  // Cap clutter, but never drop exits (or the entry that opened that trade).
  if (out.length > 48) {
    const keep = new Set<number>();
    for (let idx = 0; idx < out.length; idx++) {
      if (out[idx].side === 'exit') {
        keep.add(idx);
        for (let j = idx - 1; j >= 0; j--) {
          if (out[j].side === 'exit') break;
          keep.add(j);
          if (out[j].label === 'L' || out[j].label === 'S' || out[j].label === 'DCA') break;
        }
      }
    }
    const step = Math.ceil(out.length / 40);
    for (let idx = 0; idx < out.length; idx += step) keep.add(idx);
    return out.filter((_, idx) => keep.has(idx));
  }
  return out;
};
